import type { ConsoleEntry, ConsoleLevel } from '@feedback/shared';
import { RingBuffer } from './ring-buffer.js';

const LEVELS: ConsoleLevel[] = ['log', 'info', 'warn', 'error', 'debug'];
const MAX_ENTRIES = 50;
const MAX_MESSAGE_LENGTH = 2_000;

const buffer = new RingBuffer<ConsoleEntry>(MAX_ENTRIES);
let installed = false;
let pageLoadedAt = Date.now();

/**
 * Serialises a console argument without ever throwing. Console arguments are
 * arbitrary values from the host app — circular objects, DOM nodes, Proxies that
 * throw on property access — and a collector that breaks the page it is
 * observing is worse than no collector.
 */
function stringify(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value instanceof Error) return `${value.name}: ${value.message}`;
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';

  const primitive = typeof value;
  if (primitive !== 'object' && primitive !== 'function') return String(value);
  if (primitive === 'function') return `[Function ${(value as { name?: string }).name || 'anonymous'}]`;

  if (value instanceof Node) {
    return `<${value.nodeName.toLowerCase()}>`;
  }

  try {
    const seen = new WeakSet<object>();
    return JSON.stringify(value, (_key, val: unknown) => {
      if (typeof val === 'object' && val !== null) {
        if (seen.has(val)) return '[Circular]';
        seen.add(val);
      }
      return val;
    }) ?? String(value);
  } catch {
    return '[Unserialisable]';
  }
}

export function installConsoleCollector(): void {
  if (installed) return;
  installed = true;
  pageLoadedAt = Date.now();

  for (const level of LEVELS) {
    const original = console[level]?.bind(console);
    if (!original) continue;

    console[level] = (...args: unknown[]) => {
      try {
        buffer.push({
          level,
          message: args.map(stringify).join(' ').slice(0, MAX_MESSAGE_LENGTH),
          at: Date.now() - pageLoadedAt,
        });
      } catch {
        // Never let instrumentation break the host page's logging.
      }
      original(...args);
    };
  }
}

export function getConsoleEntries(): ConsoleEntry[] {
  return buffer.toArray();
}
